import { CheckCircleOutlined, EditOutlined, ExclamationCircleFilled, EyeOutlined, SettingOutlined, UserOutlined, TeamOutlined, CheckCircleFilled, CloseCircleFilled, UploadOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Table, Tag, Card, Space, Typography, Badge, Tooltip, Divider } from 'antd';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import { rightsService } from '../../../services/rightsService';
import AddIntermediaire from '../AddIntermediaire';
import EditIntermediaire from '../EditIntermediaire';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';
import ActionDropdown from '../../production/components/ActionDropdown';
import { useNavigate } from 'react-router-dom';
import OssanActionDropdown from '../../../components/tables/OssanActionDropdown';
import { StatCard } from '../../../components/statistics/StatCard';
import ImportIntermediairesModal from './ImportIntermediairesModal';

const { confirm } = Modal;
const { Title, Text } = Typography;

const Intermediaires = () => {
    const [intermediaires, setIntermediaires] = useState([]);
    const [selectedIntermediaire, setSelectedIntermediaire] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [reload, setReload] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [commissionData, setCommissionData] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [allCategories, setAllCategories] = useState([]);
    const [importModalOpen, setImportModalOpen] = useState(false);

    // --- Bulk selection state ---
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkLoading, setBulkLoading] = useState(false);

    const [form] = Form.useForm();

    // Thème de couleurs
    const colors = {
        primary: '#242166',
        secondary: '#1890ff',
        success: '#52c41a',
        warning: '#faad14',
        error: '#ff4d4f',
        gradient: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
        cardBg: '#ffffff',
        headerBg: '#fafafa'
    };

    const handleShow = () => setShowModal(true);
    const handleClose = () => setShowModal(false);
    const handleEditClose = () => setShowEditModal(false);
    const handleOpenImport = () => setImportModalOpen(true);
    const handleCloseImport = () => setImportModalOpen(false);

    const activateIntermediaireConfirm = (inter) => {
        confirm({
            title: `Etes-vous sûr de vouloir ${inter.active ? "désactiver" : "activer"} cet intermédiaire?`,
            icon: <ExclamationCircleFilled style={{ color: colors.warning }} />,
            content: "Toutes vos actions sont historisées!",
            okText: "Oui",
            okType: "danger",
            cancelText: "Annuler",
            okButtonProps: {
                style: {
                    background: inter.active ? colors.error : colors.success,
                    borderColor: inter.active ? colors.error : colors.success
                }
            },
            onOk() {
                activateIntermediaire(inter);
            },
        });
    };

    const activateIntermediaire = (inter) => {
        axiosInstance
            .get(`/api/intermediaire/activity/${inter.codeIntermediaire}`).then((res) => {
                inter.active ? toast("Intermédiaire désactivé avec succès!") : toast("Intermédiaire activé avec succès!");
                setReload(prev => !prev);
            }).catch(err => {
                console.log(err);
                inter.active ? toast("Erreur lors de la désactivation de l'intermédiaire") : toast("Erreur lors de l'activation de l'intermédiaire");
            });
    }

    const handleEditShow = (inter) => {
        setSelectedIntermediaire(inter);
        setShowEditModal(true);
    }

    useEffect(() => {
        axiosInstance
            .get(`/api/intermediaire`)
            .then((res) => {
                setIntermediaires(res.data);
                setSelectedRowKeys([]); // reset selection on reload
            })
            .catch((err) => {
                console.log(err);
            });

        axiosInstance.get(`/api/categorie`).then((res) => {
            setAllCategories(res.data?.filter((c) => !c.codeCategorie?.startsWith("V")));
        }).catch((err) => {
            console.log(err);
        });
    }, [showModal, reload]);

    const showCommissionsModal = (record) => {
        setSelectedIntermediaire(record);

        const existingCommissions = new Map(
            record.tauxCommission?.map(comm => [comm?.categorie?.codeCategorie, comm.taux])
        );

        const initialFormValues = {
            commissions: allCategories.map(cat => ({
                categorie: cat,
                taux: existingCommissions.get(cat.codeCategorie) || 0,
            }))
        };

        setCommissionData(initialFormValues.commissions);
        form.setFieldsValue(initialFormValues);
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setSelectedIntermediaire(null);
        setCommissionData([]);
    };

    const handleSave = (values) => {
        setLoading(true);
        axiosInstance.put(`/api/intermediaire/taux-commission`, {
            intermediaireId: selectedIntermediaire.codeId,
            commissions: values.commissions
        }).then(res => {
            toast.success("Taux de commission mis à jour avec succès!");
            setReload(prev => !prev);
            handleCancel();
            setLoading(false);
        }).catch(err => {
            console.log(err);
            toast.error("Erreur lors de la mise à jour des taux de commission");
            handleCancel();
            setLoading(false);
        });
    };

    // ─── Bulk action helpers ──────────────────────────────────────────────────

    /**
     * Executes a toggle request for a list of intermediaires codes.
     * Only calls the API for records whose current `active` status differs
     * from the desired `targetActive` value (avoids unnecessary calls).
     */
    const executeBulkToggle = (codes, targetActive) => {
        setBulkLoading(true);
        const requests = codes.map((code) =>
            axiosInstance.get(`/api/intermediaire/activity/${code}`)
        );
        Promise.allSettled(requests).then((results) => {
            const failed = results.filter(r => r.status === "rejected").length;
            const succeeded = results.filter(r => r.status === "fulfilled").length;
            if (succeeded > 0) {
                toast.success(
                    `${succeeded} intermédiaire(s) ${targetActive ? "activé(s)" : "désactivé(s)"} avec succès!`
                );
            }
            if (failed > 0) {
                toast.error(`${failed} intermédiaire(s) n'ont pas pu être traité(s).`);
            }
            setSelectedRowKeys([]);
            setReload(prev => !prev);
            setBulkLoading(false);
        });
    };

    /**
     * Shows a confirm dialog then triggers the bulk toggle.
     * @param {'activate'|'deactivate'} action
     * @param {Array}  targets  – array of intermediaire objects to act on
     */
    const confirmBulkAction = (action, targets) => {
        if (!targets.length) return;
        const isActivating = action === 'activate';
        const verb = isActivating ? "activer" : "désactiver";
        const count = targets.length;
        const label = count === intermediaires.length ? "tous les intermédiaires" : `${count} intermédiaire(s) sélectionné(s)`;

        confirm({
            title: `Confirmer l'action groupée`,
            icon: <ExclamationCircleFilled style={{ color: colors.warning }} />,
            content: (
                <span>
                    Êtes-vous sûr de vouloir <strong>{verb}</strong> {label} ?
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>Toutes vos actions sont historisées.</Text>
                </span>
            ),
            okText: `Oui, ${verb}`,
            okType: "danger",
            cancelText: "Annuler",
            okButtonProps: {
                style: {
                    background: isActivating ? colors.success : colors.error,
                    color: "white",
                    borderColor: isActivating ? colors.success : colors.error
                }
            },
            onOk() {
                // Only toggle records that need to change state
                const toChange = targets.filter(i => i.active !== isActivating);
                if (!toChange.length) {
                    toast.info("Aucun changement nécessaire, tous les intermédiaires sont déjà dans l'état souhaité.");
                    return;
                }
                executeBulkToggle(toChange.map(i => i.codeIntermediaire), isActivating);
            }
        });
    };

    // Derived helpers
    const selectedObjects = intermediaires.filter(i => selectedRowKeys.includes(i.codeId));
    const hasSelection = selectedRowKeys.length > 0;

    // ─────────────────────────────────────────────────────────────────────────

    const commissionColumns = [
        {
            title: <Text strong style={{ color: colors.primary }}>Catégorie</Text>,
            dataIndex: 'categorie',
            key: 'categorie',
            render: (text) => <Text>{text.nom}</Text>,
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Taux de Commission (%)</Text>,
            dataIndex: 'taux',
            key: 'taux',
            render: (text, record, index) => (
                <>
                    <Form.Item
                        name={['commissions', index, 'taux']}
                        initialValue={text}
                        rules={[{ required: true, message: 'Le taux est requis!' }]}
                        style={{ marginBottom: 0 }}
                    >
                        <Input
                            type="number"
                            step="0.01"
                            style={{
                                borderColor: colors.primary,
                                borderRadius: '6px'
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name={['commissions', index, 'categorie']}
                        style={{ display: 'none' }}
                    >
                        <Input type="hidden" />
                    </Form.Item>
                </>
            ),
        },
    ];

    const navigate = useNavigate();
    const canCreateIntermediaire = rightsService("HLA052");

    const columns = [
        {
            title: <Text strong style={{ color: colors.primary }}>Code</Text>,
            dataIndex: "codeIntermediaire",
            key: "codeIntermediaire",
            render: (text) => (
                <Tag style={{
                    background: colors.gradient,
                    color: 'white',
                    border: 'none',
                    fontWeight: '500',
                    padding: '4px 12px',
                    borderRadius: '6px'
                }}>
                    {text}
                </Tag>
            ),
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Type</Text>,
            dataIndex: "type",
            key: "type",
            render: (text) => (
                <Badge
                    color={colors.secondary}
                    text={<Text style={{ color: '#595959' }}>{text}</Text>}
                />
            ),
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Nom Complet</Text>,
            dataIndex: "nom",
            key: "nom",
            render: (_, record) => (
                <Space>
                    <UserOutlined style={{ color: colors.primary }} />
                    <Text strong style={{ color: '#262626' }}>
                        {record?.personne?.nom} {record?.personne?.prenom}
                    </Text>
                </Space>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Contacts</Text>,
            dataIndex: "contacts",
            key: "contacts",
            render: (_, record) => (
                <Text style={{ color: '#595959' }}>
                    {record.personne?.contact && record.personne?.contact[0]}
                </Text>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Statut</Text>,
            dataIndex: "active",
            key: "active",
            render: (_, record) => (
                <Tag
                    icon={record.active ? <CheckCircleFilled /> : <CloseCircleFilled />}
                    color={record.active ? "success" : "error"}
                    style={{
                        borderRadius: '6px',
                        padding: '4px 12px',
                        fontWeight: '500'
                    }}
                >
                    {record.active ? "Actif" : "Inactif"}
                </Tag>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Actions</Text>,
            dataIndex: "action",
            key: "action",
            render: (_, record) => {
                const actions = [
                    {
                        key: "details",
                        label: "Détails",
                        icon: <EyeOutlined />,
                        onClick: (e) => {
                            navigate(`/intermediaire/${record.codeId}`);
                        },
                    },
                    rightsService("HPE041") &&
                    record.active && {
                        key: "edit",
                        label: "Modifier",
                        icon: <EditOutlined />,
                        onClick: (info) => {
                            info?.domEvent?.preventDefault?.();
                            info?.domEvent?.stopPropagation?.();
                            handleEditShow(record);
                        },
                    },
                    rightsService("HPE041") && {
                        key: "reset",
                        label: `${record?.active ? "Désactiver" : "Activer"}`,
                        icon: <CheckCircleOutlined />,
                        onClick: (info) => {
                            info?.domEvent?.preventDefault?.();
                            info?.domEvent?.stopPropagation?.();
                            activateIntermediaireConfirm(record)
                        },
                    },
                ].filter(Boolean);

                return <OssanActionDropdown actions={actions} />;
            }
        },
    ];

    const poolColumns = [
        {
            title: <Text strong style={{ color: colors.primary }}>Code</Text>,
            dataIndex: "codeIntermediaire",
            key: "codeIntermediaire",
            render: (text) => (
                <Tag style={{
                    background: colors.gradient,
                    color: 'white',
                    border: 'none',
                    fontWeight: '500',
                    padding: '4px 12px',
                    borderRadius: '6px'
                }}>
                    {text}
                </Tag>
            ),
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Type</Text>,
            dataIndex: "type",
            key: "type",
            render: (text) => (
                <Badge
                    color={colors.secondary}
                    text={<Text style={{ color: '#595959' }}>{text}</Text>}
                />
            ),
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Nom</Text>,
            dataIndex: "nom",
            key: "nom",
            render: (_, record) => (
                <Space>
                    <UserOutlined style={{ color: colors.primary }} />
                    <Text strong style={{ color: '#262626' }}>
                        {record.personne?.nom} {record.personne?.prenom}
                    </Text>
                </Space>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Organisme</Text>,
            dataIndex: "organisme",
            key: "organisme",
            render: (_, record) => (
                <Text style={{ color: '#595959' }}>
                    {record?.organisme?.raisonSociale || "-"}
                </Text>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Contacts</Text>,
            dataIndex: "contacts",
            key: "contacts",
            render: (_, record) => (
                <Text style={{ color: '#595959' }}>
                    {record.contact && record.contact[0]}
                </Text>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Statut</Text>,
            dataIndex: "active",
            key: "active",
            render: (_, record) => (
                <Tag
                    icon={record.active ? <CheckCircleFilled /> : <CloseCircleFilled />}
                    color={record.active ? "success" : "error"}
                    style={{
                        borderRadius: '6px',
                        padding: '4px 12px',
                        fontWeight: '500'
                    }}
                >
                    {record.active ? "Actif" : "Inactif"}
                </Tag>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Actions</Text>,
            dataIndex: "action",
            key: "action",
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        shape="circle"
                        onClick={() => handleEditShow(record)}
                        style={{
                            background: colors.secondary,
                            borderColor: colors.secondary
                        }}
                    />
                    <Button
                        type="primary"
                        icon={record?.active ? <CloseCircleFilled /> : <CheckCircleFilled />}
                        shape="circle"
                        onClick={() => activateIntermediaireConfirm(record)}
                        style={{
                            background: !record?.active ? colors.success : colors.error,
                            borderColor: !record?.active ? colors.success : colors.error
                        }}
                    />
                </Space>
            )
        },
    ];

    // Calcul des statistiques
    const stats = {
        total: intermediaires.length,
        actifs: intermediaires.filter(i => i.active).length,
        inactifs: intermediaires.filter(i => !i.active).length
    };

    // Row selection config (uses codeId as the row key)
    const rowSelection = rightsService("HPE041") ? {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        getCheckboxProps: (record) => ({
            name: record.codeIntermediaire,
        }),
    } : undefined;

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"COURTIERS"} />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            {/* Header Section */}
                            <div style={{ marginBottom: '24px' }}>
                                <Title level={2} style={{
                                    background: colors.gradient,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    marginBottom: '8px'
                                }}>
                                    Gestion des Intermédiaires
                                </Title>
                                <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
                                    Gérez et suivez tous vos intermédiaires en un seul endroit
                                </Text>
                            </div>

                            {/* Statistics Cards */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                gap: '20px',
                                marginBottom: '24px'
                            }}>
                                <StatCard
                                    title="Total Intermédiaires"
                                    value={stats.total}
                                    subtitle="Intermédiaires enregistrés"
                                    icon="bx bx-group"
                                    iconBg={colors.gradient}
                                    gradient={colors.gradient}
                                />
                                <StatCard
                                    title="Intermédiaires Actifs"
                                    value={stats.actifs}
                                    subtitle="Actuellement en service"
                                    icon="bx bx-check-circle"
                                    iconBg="linear-gradient(135deg, #52c41a 0%, #73d13d 100%)"
                                    gradient="linear-gradient(135deg, #52c41a 0%, #73d13d 100%)"
                                />
                                <StatCard
                                    title="Intermédiaires Inactifs"
                                    value={stats.inactifs}
                                    subtitle="Temporairement désactivés"
                                    icon="bx bx-x-circle"
                                    iconBg="linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)"
                                    gradient="linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)"
                                />
                            </div>

                            {/* Action Buttons */}
                            {rightsService("HLA052") && (
                                <div style={{ marginBottom: '20px' }}>
                                    <Space wrap>
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<i className="bx bx-plus" style={{ fontSize: '18px' }}></i>}
                                            onClick={handleShow}
                                            disabled={LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL"}
                                            style={{
                                                background: colors.gradient,
                                                border: 'none',
                                                height: '44px',
                                                borderRadius: '8px',
                                                fontWeight: '500',
                                                boxShadow: '0 4px 12px rgba(36, 33, 102, 0.2)'
                                            }}
                                        >
                                            Ajouter un intermédiaire
                                        </Button>
                                        {canCreateIntermediaire && (
                                            <Button
                                                icon={<UploadOutlined />}
                                                size="large"
                                                onClick={handleOpenImport}
                                                disabled={LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL"}
                                                style={{
                                                    borderRadius: '8px',
                                                    borderColor: colors.primary,
                                                    color: colors.primary,
                                                    height: '44px'
                                                }}
                                            >
                                                Importer (Excel)
                                            </Button>
                                        )}
                                    </Space>
                                </div>
                            )}

                            {/* ── Bulk action toolbar ── */}
                            {rightsService("HPE041") && (
                                <Card
                                    size="small"
                                    style={{
                                        marginBottom: '16px',
                                        borderRadius: '10px',
                                        border: `1px solid ${hasSelection ? colors.primary : '#f0f0f0'}`,
                                        background: hasSelection ? '#f5f5ff' : '#fafafa',
                                        transition: 'all 0.3s ease'
                                    }}
                                    bodyStyle={{ padding: '12px 16px' }}
                                >
                                    <Space wrap align="center">
                                        <Text strong style={{ color: colors.primary, fontSize: 13 }}>
                                            Actions groupées :
                                        </Text>

                                        {/* ── "Tout" actions ── */}
                                        <Tooltip title="Activer tous les intermédiaires de la liste">
                                            <Button
                                                size="middle"
                                                loading={bulkLoading}
                                                icon={<CheckCircleFilled style={{ color: colors.success }} />}
                                                onClick={() => confirmBulkAction('activate', intermediaires)}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Activer tout
                                            </Button>
                                        </Tooltip>

                                        <Tooltip title="Désactiver tous les intermédiaires de la liste">
                                            <Button
                                                size="middle"
                                                loading={bulkLoading}
                                                icon={<CloseCircleFilled style={{ color: colors.error }} />}
                                                onClick={() => confirmBulkAction('deactivate', intermediaires)}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Désactiver tout
                                            </Button>
                                        </Tooltip>

                                        <Divider type="vertical" style={{ height: 24, borderColor: '#d9d9d9' }} />

                                        {/* ── Selection actions ── */}
                                        <Tooltip title={!hasSelection ? "Cochez d'abord des lignes dans le tableau" : `Activer les ${selectedRowKeys.length} intermédiaire(s) sélectionné(s)`}>
                                            <Button
                                                size="middle"
                                                loading={bulkLoading}
                                                disabled={!hasSelection}
                                                icon={<CheckCircleFilled />}
                                                onClick={() => confirmBulkAction('activate', selectedObjects)}
                                                style={{
                                                    borderRadius: '6px',
                                                    background: hasSelection ? colors.success : undefined,
                                                    borderColor: hasSelection ? colors.success : undefined,
                                                    color: hasSelection ? '#fff' : undefined,
                                                    fontWeight: hasSelection ? '500' : undefined
                                                }}
                                            >
                                                Activer la sélection
                                                {hasSelection && (
                                                    <Tag
                                                        color="white"
                                                        style={{
                                                            color: colors.success,
                                                            marginLeft: 6,
                                                            borderRadius: '10px',
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                            lineHeight: '18px',
                                                            padding: '0 7px'
                                                        }}
                                                    >
                                                        {selectedRowKeys.length}
                                                    </Tag>
                                                )}
                                            </Button>
                                        </Tooltip>

                                        <Tooltip title={!hasSelection ? "Cochez d'abord des lignes dans le tableau" : `Désactiver les ${selectedRowKeys.length} intermédiaire(s) sélectionné(s)`}>
                                            <Button
                                                size="middle"
                                                loading={bulkLoading}
                                                disabled={!hasSelection}
                                                icon={<CloseCircleFilled />}
                                                onClick={() => confirmBulkAction('deactivate', selectedObjects)}
                                                style={{
                                                    borderRadius: '6px',
                                                    background: hasSelection ? colors.error : undefined,
                                                    borderColor: hasSelection ? colors.error : undefined,
                                                    color: hasSelection ? '#fff' : undefined,
                                                    fontWeight: hasSelection ? '500' : undefined
                                                }}
                                            >
                                                Désactiver la sélection
                                                {hasSelection && (
                                                    <Tag
                                                        color="white"
                                                        style={{
                                                            color: colors.error,
                                                            marginLeft: 6,
                                                            borderRadius: '10px',
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                            lineHeight: '18px',
                                                            padding: '0 7px'
                                                        }}
                                                    >
                                                        {selectedRowKeys.length}
                                                    </Tag>
                                                )}
                                            </Button>
                                        </Tooltip>

                                        {hasSelection && (
                                            <Button
                                                size="middle"
                                                type="link"
                                                onClick={() => setSelectedRowKeys([])}
                                                style={{ color: '#8c8c8c', padding: '0 4px' }}
                                            >
                                                Désélectionner tout
                                            </Button>
                                        )}
                                    </Space>
                                </Card>
                            )}

                            {/* Table Card */}
                            <Card
                                style={{
                                    borderRadius: '12px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: '1px solid #f0f0f0'
                                }}
                                bodyStyle={{ padding: 0 }}
                            >
                                <Table
                                    rowKey="codeId"
                                    rowSelection={rowSelection}
                                    columns={LOGGED_USER.profil?.organisme?.typeOrganisme === "POOL" ? poolColumns : columns}
                                    dataSource={intermediaires}
                                    onRow={(record) => ({
                                        onClick: () => {
                                            showCommissionsModal(record);
                                        },
                                        style: { cursor: 'pointer' }
                                    })}
                                    pagination={{
                                        pageSize: 10,
                                        showSizeChanger: true,
                                        showTotal: (total) => `Total: ${total} intermédiaires`,
                                        style: { padding: '16px' }
                                    }}
                                    style={{
                                        borderRadius: '12px'
                                    }}
                                />
                            </Card>

                            {/* Commission Modal */}
                            <Modal
                                title={
                                    <div style={{
                                        padding: '8px 0',
                                        borderBottom: `2px solid ${colors.primary}`
                                    }}>
                                        <Title level={4} style={{
                                            margin: 0,
                                            color: colors.primary
                                        }}>
                                            <SettingOutlined style={{ marginRight: '8px' }} />
                                            Edition des taux de commissions
                                        </Title>
                                        <Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                                            {selectedIntermediaire?.personne?.nom} {selectedIntermediaire?.personne?.prenom}
                                        </Text>
                                    </div>
                                }
                                open={isModalVisible}
                                onCancel={handleCancel}
                                footer={null}
                                width={700}
                                style={{ top: 20 }}
                            >
                                {selectedIntermediaire && (
                                    <Form form={form} onFinish={handleSave}>
                                        <div style={{ margin: '20px 0' }}>
                                            <Table
                                                columns={commissionColumns}
                                                dataSource={commissionData}
                                                pagination={false}
                                                size="middle"
                                                style={{
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </div>
                                        <div style={{
                                            marginTop: '24px',
                                            textAlign: 'right',
                                            paddingTop: '16px',
                                            borderTop: '1px solid #f0f0f0'
                                        }}>
                                            <Space>
                                                <Button
                                                    onClick={handleCancel}
                                                    size="large"
                                                    style={{
                                                        borderRadius: '6px',
                                                        minWidth: '100px'
                                                    }}
                                                >
                                                    Annuler
                                                </Button>
                                                <Button
                                                    loading={loading}
                                                    type="primary"
                                                    htmlType="submit"
                                                    size="large"
                                                    style={{
                                                        background: colors.gradient,
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        minWidth: '120px',
                                                        boxShadow: '0 2px 8px rgba(36, 33, 102, 0.2)'
                                                    }}
                                                >
                                                    Sauvegarder
                                                </Button>
                                            </Space>
                                        </div>
                                    </Form>
                                )}
                            </Modal>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            <AddIntermediaire
                show={showModal}
                handleClose={handleClose}
            />
            <EditIntermediaire
                show={showEditModal}
                handleClose={handleEditClose}
                intermediaire={selectedIntermediaire}
            />
            <ImportIntermediairesModal
                open={importModalOpen && canCreateIntermediaire}
                onClose={handleCloseImport}
                colors={colors}
                onImported={() => setReload((prev) => !prev)}
            />

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
}

export default Intermediaires;